import type { CSSProperties } from 'react'

import { LOGO_SVG, LOGO_VIEWBOX } from '@/lib/logo-svg'
import { cn } from '@/lib/utils'

/**
 * Flat FLOU wordmark that inherits `currentColor`.
 *
 * The hero treatments (`ChromeLogo`, `ChromeLogo3D`) paint the same outlines
 * through WebGL — right for a hero, far too expensive for chrome that sits on
 * every page. This reuses the shared `LOGO_SVG` so the shapes can't drift, and
 * only swaps its baked-in black fill for the current text colour.
 */
const INLINE_SVG = LOGO_SVG.replace('fill="#000"', 'fill="currentColor"')

// Strip the outer <svg> wrapper: we re-declare it here so the element can carry
// our own sizing, a11y attributes and colour, rather than the raw file's.
const INNER = INLINE_SVG.replace(/^[\s\S]*?<svg[^>]*>/, '').replace(/<\/svg>\s*$/, '')

/**
 * `style` and `aria-hidden` exist for the backdrops that stack or tint copies
 * of this mark — today `AuthMarkBackdrop`, which lays a blurred bloom under the
 * face so the sign-in card gets light spilling out of the letterforms. Neither
 * copy may announce itself as a second "FLOU" to a screen reader, so a hidden
 * one drops `role="img"` with its label: an `aria-hidden` image role is a
 * contradiction some readers resolve by announcing it anyway.
 */
export function FlouWordmark({
  className,
  style,
  'aria-hidden': hidden,
}: {
  className?: string
  style?: CSSProperties
  'aria-hidden'?: boolean | 'true' | 'false'
}) {
  const decorative = hidden === true || hidden === 'true'

  return (
    <svg
      viewBox={`0 0 ${LOGO_VIEWBOX.width} ${LOGO_VIEWBOX.height}`}
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : 'FLOU'}
      aria-hidden={decorative ? 'true' : undefined}
      style={style}
      className={cn('h-6 w-auto', className)}
      // The paths are a build-time constant from `lib/logo-svg.ts`, never user
      // input — this is the one way to keep a single copy of the outlines.
      // biome-ignore lint/security/noDangerouslySetInnerHtml: static in-repo asset, see above
      dangerouslySetInnerHTML={{ __html: INNER }}
    />
  )
}
